import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FiArrowLeft,
  FiChevronDown,
  FiDownload,
  FiPlus,
  FiSave,
  FiTrash2,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { productionTrackingRowsService } from "../services/api/productionTrackingRowsService";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "./StockInventoryItemsManage.css";
import "./ProjectsProductionPerformanceDataManage.css";

const ACCOUNT_GROUPS = ["Labor", "INFO", "PRODQTY", "RENTEQUIP", "Inventory"];
const PP_SELECTED_PROJECT_KEY = "ppProductionDataSelectedProject";

const EMPTY_ROW = {
  scope: "",
  activity_code: "",
  account_group: "Labor",
  description: "",
  code: "",
  quantity: "",
  uom: "",
};

function readStoredSelectedProject() {
  try {
    const raw = localStorage.getItem(PP_SELECTED_PROJECT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const projectNo = parsed?.projectNo != null ? String(parsed.projectNo).trim() : "";
    if (!projectNo) return null;
    return {
      projectNo,
      name: String(parsed.name || projectNo).trim() || projectNo,
      opportunityId: parsed.opportunityId ? String(parsed.opportunityId) : "",
    };
  } catch {
    return null;
  }
}

function writeStoredSelectedProject(project) {
  try {
    if (!project?.projectNo) {
      localStorage.removeItem(PP_SELECTED_PROJECT_KEY);
      return;
    }
    localStorage.setItem(
      PP_SELECTED_PROJECT_KEY,
      JSON.stringify({
        projectNo: project.projectNo,
        name: project.name || project.projectNo,
        opportunityId: project.opportunityId || "",
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 =
        result && String(result).indexOf("base64,") >= 0
          ? String(result).split("base64,")[1]
          : result;
      resolve(base64 || "");
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function mapOpportunityToProject(record) {
  const projectNo = String(record?.Project__r?.Project_System_Wide_ID__c || "").trim();
  const name = String(record?.Project__r?.Name || record?.Name || "").trim();
  if (!projectNo && !name) return null;
  return {
    projectNo,
    name: name || projectNo,
    opportunityId: record?.Id || "",
  };
}

function toEditableRows(rows) {
  return (Array.isArray(rows) ? rows : []).map((row, index) => {
    const activityCode = String(row.activity_code || "").trim();
    const code = String(row.code || "").trim();
    const accountGroup = String(row.account_group || "").trim();
    const isLabor = accountGroup.toUpperCase() === "LABOR";
    // Non-Labor rows often store activity code in `code` as a fallback — hide that in the Labor Tracking Code field.
    const laborTrackingCode =
      isLabor || (code && code !== activityCode) ? code : "";
    return {
      localKey: row.id != null ? `id-${row.id}` : `new-${index}-${Date.now()}`,
      id: row.id ?? null,
      scope: row.scope || "",
      activity_code: activityCode,
      account_group: accountGroup,
      description: row.description || "",
      code: laborTrackingCode,
      quantity: row.quantity == null ? "" : String(row.quantity),
      uom: row.uom || "",
    };
  });
}

function rowsForSave(rows) {
  return (rows || []).map((row) => ({
    scope: String(row.scope || "").trim(),
    activity_code: String(row.activity_code || "").trim() || null,
    account_group: String(row.account_group || "").trim(),
    description: String(row.description || "").trim() || null,
    code: String(row.code || "").trim(),
    quantity:
      row.quantity === "" || row.quantity == null
        ? 0
        : Number(row.quantity),
    uom: String(row.uom || "").trim() || null,
  }));
}

function clampProjectPopupPos(rect, preferredWidth = 360) {
  const margin = 8;
  const width = Math.min(
    Math.max(rect.width, preferredWidth),
    Math.max(margin * 2, window.innerWidth - margin * 2)
  );
  const left = Math.min(
    Math.max(margin, rect.left),
    Math.max(margin, window.innerWidth - width - margin)
  );
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const preferredHeight = 420;
  const openAbove = spaceBelow < preferredHeight && rect.top > spaceBelow;
  if (openAbove) {
    return {
      left,
      bottom: Math.max(margin, window.innerHeight - rect.top + 8),
      top: null,
      width,
    };
  }
  return {
    left,
    top: Math.min(rect.bottom + 8, window.innerHeight - margin - 120),
    bottom: null,
    width,
  };
}

function ProjectSelectButton({
  projects,
  uploadedByProject,
  selectedProjectNo,
  onSelect,
  disabled,
  projectsLoading,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [popupPos, setPopupPos] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const btnRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const selected = projects.find((p) => p.projectNo === selectedProjectNo) || null;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const list = projects.filter((p) => p.projectNo);
    if (!term) return list;
    return list.filter((p) => `${p.name} ${p.projectNo}`.toLowerCase().includes(term));
  }, [projects, query]);

  const handlePickProject = useCallback((project) => {
    if (!project) return;
    onSelect(project);
    setOpen(false);
    setActiveIndex(0);
  }, [onSelect]);

  useEffect(() => {
    if (!open) {
      setPopupPos(null);
      setActiveIndex(0);
      return undefined;
    }
    setQuery("");
    setActiveIndex(0);
    const updatePos = () => {
      const el = btnRef.current;
      if (!el) return;
      setPopupPos(clampProjectPopupPos(el.getBoundingClientRect(), 380));
    };
    updatePos();
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 40);
    window.addEventListener("resize", updatePos);
    window.addEventListener("scroll", updatePos, true);
    return () => {
      window.clearTimeout(focusTimer);
      window.removeEventListener("resize", updatePos);
      window.removeEventListener("scroll", updatePos, true);
    };
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        return;
      }
      if (event.key === "ArrowDown" && filtered.length > 0) {
        event.preventDefault();
        setActiveIndex((prev) => (prev + 1) % filtered.length);
        return;
      }
      if (event.key === "ArrowUp" && filtered.length > 0) {
        event.preventDefault();
        setActiveIndex((prev) => (prev - 1 + filtered.length) % filtered.length);
        return;
      }
      if (event.key === "Enter" && filtered[activeIndex]) {
        event.preventDefault();
        handlePickProject(filtered[activeIndex]);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, filtered, activeIndex, handlePickProject]);

  useEffect(() => {
    if (!open || !listRef.current) return;
    const activeEl = listRef.current.querySelector(
      `[data-pp-project-index="${activeIndex}"]`
    );
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open, filtered.length]);

  return (
    <div className="pp-project-select">
      <button
        ref={btnRef}
        type="button"
        className="stock-inv-btn stock-inv-btn-ghost pp-project-select-btn"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{selected ? "Change Project" : "Select Project"}</span>
        <FiChevronDown />
      </button>

      {open &&
        popupPos &&
        createPortal(
          <>
            <div className="pp-project-popup-backdrop" onClick={() => setOpen(false)} />
            <div
              className="pp-project-popup"
              style={{
                left: popupPos.left,
                width: popupPos.width,
                ...(popupPos.top != null
                  ? { top: popupPos.top }
                  : { bottom: popupPos.bottom }),
              }}
              role="dialog"
              aria-label="Select Project"
            >
              <div className="pp-project-popup-header">
                <div>
                  <h3>Select Project</h3>
                  <p>
                    Total:{" "}
                    <strong>{projectsLoading ? "…" : projects.length}</strong>
                    {query.trim() ? ` · Showing ${filtered.length}` : ""}
                    {!query.trim() && uploadedByProject.size > 0
                      ? ` · ${uploadedByProject.size} with uploaded data`
                      : ""}
                  </p>
                </div>
                <button
                  type="button"
                  className="pp-project-popup-close"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                >
                  <FiX />
                </button>
              </div>

              <div className="pp-project-popup-search">
                <input
                  ref={searchRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name or project id…"
                  aria-autocomplete="list"
                  aria-controls="pp-project-popup-listbox"
                />
              </div>

              <div
                id="pp-project-popup-listbox"
                className="pp-project-popup-list"
                ref={listRef}
                role="listbox"
              >
                {projectsLoading && projects.length === 0 ? (
                  <p className="pp-project-popup-empty">Loading projects…</p>
                ) : filtered.length === 0 ? (
                  <p className="pp-project-popup-empty">No projects match that search.</p>
                ) : (
                  filtered.map((project, index) => {
                    const uploadedCount = uploadedByProject.get(project.projectNo) || 0;
                    const isSelected = project.projectNo === selectedProjectNo;
                    const isKeyboardActive = index === activeIndex;
                    return (
                      <button
                        key={project.projectNo}
                        type="button"
                        role="option"
                        aria-selected={isKeyboardActive}
                        data-pp-project-index={index}
                        className={`pp-project-popup-item${isSelected ? " active" : ""}${
                          isKeyboardActive ? " pp-project-popup-item--keyboard-active" : ""
                        }`}
                        onClick={() => handlePickProject(project)}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <span className="pp-project-popup-item-name">{project.name}</span>
                        <span className="pp-project-popup-item-id">{project.projectNo}</span>
                        {uploadedCount > 0 ? (
                          <span className="pp-project-popup-item-meta">
                            {uploadedCount} uploaded row{uploadedCount === 1 ? "" : "s"}
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
}

function ProjectsProductionPerformanceDataManage({ onBack, onToast }) {
  const { authFetch, isReady } = useAuthFetch();
  const fileInputRef = useRef(null);

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [uploadedProjects, setUploadedProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(() => readStoredSelectedProject());
  const restoredProjectNoRef = useRef(selectedProject?.projectNo || "");

  const [rows, setRows] = useState([]);
  const [baselineJson, setBaselineJson] = useState("[]");
  const [rowsLoading, setRowsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [rowDeleteIndex, setRowDeleteIndex] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);

  const uploadedByProject = useMemo(() => {
    const map = new Map();
    uploadedProjects.forEach((item) => {
      map.set(item.projectNo, item.rowCount);
    });
    return map;
  }, [uploadedProjects]);

  const isDirty = useMemo(() => {
    try {
      return JSON.stringify(rowsForSave(rows)) !== baselineJson;
    } catch {
      return true;
    }
  }, [rows, baselineJson]);

  const loadUploadedProjects = useCallback(async () => {
    try {
      const response = await productionTrackingRowsService.listProjectsWithRows();
      setUploadedProjects(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load uploaded projects list:", error);
    }
  }, []);

  const loadSalesforceProjects = useCallback(async () => {
    if (!isReady) return;
    setProjectsLoading(true);
    try {
      const cached = localStorage.getItem("projectsList");
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed)) {
            const mapped = parsed
              .map(mapOpportunityToProject)
              .filter(Boolean)
              .filter((p, index, list) =>
                list.findIndex((item) => item.projectNo === p.projectNo) === index
              )
              .sort((a, b) => a.name.localeCompare(b.name));
            setProjects(mapped);
          }
        } catch {
          /* ignore cache parse errors */
        }
      }

      const response = await authFetch(
        `${process.env.REACT_APP_BACKEND_BASEURL}/api/salesforce/all-opportunities`
      );
      if (!response.ok) {
        throw new Error(`Failed to load projects (${response.status})`);
      }
      const data = await response.json();
      const records = Array.isArray(data?.records) ? data.records : [];
      const mapped = records
        .filter((project) => project.Project__r !== null)
        .map(mapOpportunityToProject)
        .filter(Boolean)
        .filter((p, index, list) =>
          list.findIndex((item) => item.projectNo === p.projectNo) === index
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      setProjects(mapped);
    } catch (error) {
      console.error("Failed to load Salesforce projects:", error);
      onToast?.(error.message || "Failed to load projects list.");
    } finally {
      setProjectsLoading(false);
    }
  }, [authFetch, isReady, onToast]);

  useEffect(() => {
    loadSalesforceProjects();
    loadUploadedProjects();
  }, [loadSalesforceProjects, loadUploadedProjects]);

  // Merge uploaded-only project codes that may not be in the SF list.
  const allProjects = useMemo(() => {
    const map = new Map(projects.map((p) => [p.projectNo, p]));
    uploadedProjects.forEach((item) => {
      if (!map.has(item.projectNo)) {
        map.set(item.projectNo, {
          projectNo: item.projectNo,
          name: item.projectNo,
          opportunityId: "",
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [projects, uploadedProjects]);

  const loadRowsForProject = useCallback(async (projectNo) => {
    if (!projectNo) {
      setRows([]);
      setBaselineJson("[]");
      setLoadError("");
      return;
    }
    setRowsLoading(true);
    setLoadError("");
    try {
      const response = await productionTrackingRowsService.getRows(projectNo);
      const nextRows = toEditableRows(response?.data?.rows || []);
      setRows(nextRows);
      setBaselineJson(JSON.stringify(rowsForSave(nextRows)));
    } catch (error) {
      console.error("Failed to load tracking rows:", error);
      setRows([]);
      setBaselineJson("[]");
      setLoadError(error?.response?.data?.message || "Failed to load uploaded Excel data.");
    } finally {
      setRowsLoading(false);
    }
  }, []);

  // Restore table data for the project remembered across refresh.
  useEffect(() => {
    const projectNo = restoredProjectNoRef.current;
    if (!projectNo) return undefined;
    restoredProjectNoRef.current = "";
    loadRowsForProject(projectNo);
    return undefined;
  }, [loadRowsForProject]);

  // Prefer Salesforce name once the projects list is available.
  useEffect(() => {
    if (!selectedProject?.projectNo || allProjects.length === 0) return;
    const match = allProjects.find((p) => p.projectNo === selectedProject.projectNo);
    if (!match) return;
    if (
      match.name === selectedProject.name &&
      (match.opportunityId || "") === (selectedProject.opportunityId || "")
    ) {
      return;
    }
    setSelectedProject(match);
    writeStoredSelectedProject(match);
  }, [allProjects, selectedProject]);

  const handleSelectProject = async (project) => {
    if (!project?.projectNo) return;
    if (project.projectNo === selectedProject?.projectNo) return;
    if (isDirty && selectedProject) {
      const leave = window.confirm(
        "You have unsaved changes. Discard them and switch projects?"
      );
      if (!leave) return;
    }
    setSelectedProject(project);
    writeStoredSelectedProject(project);
    await loadRowsForProject(project.projectNo);
  };

  const handleClearProject = () => {
    if (!selectedProject) return;
    if (isDirty) {
      const leave = window.confirm(
        "You have unsaved changes. Discard them and clear the selection?"
      );
      if (!leave) return;
    }
    setSelectedProject(null);
    writeStoredSelectedProject(null);
    setRows([]);
    setBaselineJson("[]");
    setLoadError("");
  };

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        ...EMPTY_ROW,
        localKey: `new-${Date.now()}-${prev.length}`,
        id: null,
      },
    ]);
  };

  const confirmDeleteRow = () => {
    if (rowDeleteIndex == null) return;
    setRows((prev) => prev.filter((_, i) => i !== rowDeleteIndex));
    setRowDeleteIndex(null);
  };

  const handleSave = async () => {
    if (!selectedProject?.projectNo) return;
    const payload = rowsForSave(rows);
    const invalid = payload.find((r) => !r.scope || !r.account_group);
    if (invalid) {
      onToast?.("Each row needs Activity and Account Group before saving.");
      return;
    }
    setIsSaving(true);
    try {
      const response = await productionTrackingRowsService.saveRows(
        selectedProject.projectNo,
        payload
      );
      const nextRows = toEditableRows(response?.data?.rows || []);
      setRows(nextRows);
      setBaselineJson(JSON.stringify(rowsForSave(nextRows)));
      onToast?.(response?.message || "Production performance data saved.");
      await loadUploadedProjects();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to save data.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadClick = () => {
    if (!selectedProject?.projectNo) {
      onToast?.("Select a project first.");
      return;
    }
    setUploadDragOver(false);
    setUploadOpen(true);
  };

  const closeUploadModal = () => {
    if (isUploading) return;
    setUploadOpen(false);
    setUploadDragOver(false);
  };

  const processUploadFile = async (file) => {
    if (!file || !selectedProject?.projectNo) return;
    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      onToast?.("Please choose an .xlsx or .xls file.");
      return;
    }
    if (isDirty) {
      const proceed = window.confirm(
        "Uploading will replace the current rows for this project (including unsaved edits). Continue?"
      );
      if (!proceed) return;
    }
    setIsUploading(true);
    try {
      const fileBase64 = await readFileAsBase64(file);
      const response = await productionTrackingRowsService.uploadExcel(
        selectedProject.projectNo,
        fileBase64
      );
      onToast?.(response?.message || "Excel uploaded.");
      setUploadOpen(false);
      setUploadDragOver(false);
      await loadRowsForProject(selectedProject.projectNo);
      await loadUploadedProjects();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Upload failed.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    await processUploadFile(file);
  };

  useEffect(() => {
    if (!uploadOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !isUploading) {
        setUploadOpen(false);
        setUploadDragOver(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [uploadOpen, isUploading]);

  const handleUploadDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!selectedProject || isUploading) return;
    event.dataTransfer.dropEffect = "copy";
    setUploadDragOver(true);
  };

  const handleUploadDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setUploadDragOver(false);
  };

  const handleUploadDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setUploadDragOver(false);
    if (!selectedProject || isUploading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) await processUploadFile(file);
  };

  const handleDownload = async () => {
    if (!selectedProject?.projectNo || rows.length === 0) return;
    setIsExporting(true);
    try {
      if (isDirty) {
        const proceed = window.confirm(
          "You have unsaved edits. Download the last saved Excel data from the database?"
        );
        if (!proceed) {
          setIsExporting(false);
          return;
        }
      }
      const blob = await productionTrackingRowsService.downloadExcel(
        selectedProject.projectNo
      );
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `ProductionPerformance_${selectedProject.projectNo}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      onToast?.("Excel file downloaded.");
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to download Excel.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!selectedProject?.projectNo) return;
    setIsDeletingAll(true);
    try {
      const response = await productionTrackingRowsService.deleteAllRows(
        selectedProject.projectNo
      );
      setRows([]);
      setBaselineJson("[]");
      setDeleteAllOpen(false);
      onToast?.(response?.message || "Uploaded data deleted.");
      await loadUploadedProjects();
    } catch (error) {
      onToast?.(error?.response?.data?.message || "Failed to delete data.");
    } finally {
      setIsDeletingAll(false);
    }
  };

  return (
    <div className="config-sub-view fade-in stock-inv-manage pp-data-manage">
      <div className="sub-view-nav" onClick={onBack}>
        <FiArrowLeft /> Back to Configuration Engine
      </div>

      <header className="sub-view-header stock-inv-header">
        <div className="header-icon-badge">
          <FiUploadCloud />
        </div>
        <div className="stock-inv-header-copy">
          <h2>Manage Projects Production Performance Data</h2>
          {selectedProject ? (
            <div className="pp-header-selected-project">
              <span className="pp-header-selected-label">Project</span>
              <span className="pp-header-selected-name">{selectedProject.name}</span>
              <span className="pp-header-selected-id">{selectedProject.projectNo}</span>
              {isDirty ? (
                <span className="pp-header-selected-dirty">
                  <span className="pp-dirty-dot" /> Unsaved changes
                </span>
              ) : null}
              <button
                type="button"
                className="pp-header-clear-project"
                onClick={handleClearProject}
                title="Clear selection and start fresh"
              >
                <FiX /> Clear
              </button>
            </div>
          ) : (
            <p>
              Select a project to view, edit, save, upload, download, or delete its Production
              Performance Excel data.
            </p>
          )}
        </div>
        <div className="stock-inv-header-actions">
          <ProjectSelectButton
            projects={allProjects}
            uploadedByProject={uploadedByProject}
            selectedProjectNo={selectedProject?.projectNo || ""}
            onSelect={handleSelectProject}
            disabled={projectsLoading && allProjects.length === 0}
            projectsLoading={projectsLoading}
          />
          <button
            type="button"
            className="stock-inv-btn stock-inv-btn-ghost"
            onClick={handleUploadClick}
            disabled={!selectedProject || isUploading || rowsLoading}
          >
            <FiUploadCloud /> {isUploading ? "Uploading…" : "Upload Excel"}
          </button>
          <button
            type="button"
            className="stock-inv-btn stock-inv-btn-ghost"
            onClick={handleDownload}
            disabled={!selectedProject || rows.length === 0 || isExporting || rowsLoading}
          >
            <FiDownload /> {isExporting ? "Downloading…" : "Download Excel"}
          </button>
          <button
            type="button"
            className="stock-inv-btn stock-inv-btn-ghost"
            onClick={addRow}
            disabled={!selectedProject || rowsLoading}
          >
            <FiPlus /> Add row
          </button>
          <button
            type="button"
            className="stock-inv-btn stock-inv-btn-ghost pp-delete-all-btn"
            onClick={() => setDeleteAllOpen(true)}
            disabled={
              !selectedProject ||
              rowsLoading ||
              (rows.length === 0 && !uploadedByProject.get(selectedProject.projectNo))
            }
          >
            <FiTrash2 /> Delete all data
          </button>
          <button
            type="button"
            className="stock-inv-btn stock-inv-btn-primary"
            onClick={handleSave}
            disabled={!selectedProject || !isDirty || isSaving || rowsLoading}
          >
            <FiSave /> {isSaving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="pp-upload-hidden"
        onChange={handleUploadFile}
      />

      {uploadOpen &&
        createPortal(
          <div
            className="pp-upload-modal-backdrop"
            onClick={closeUploadModal}
            onDragOver={handleUploadDragOver}
            onDrop={handleUploadDrop}
          >
            <div
              className="pp-upload-modal"
              role="dialog"
              aria-modal="true"
              aria-label="Upload Excel"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="pp-upload-modal-header">
                <div>
                  <h3>Upload Excel</h3>
                  <p>
                    {selectedProject
                      ? `${selectedProject.name} · ${selectedProject.projectNo}`
                      : "Select a project first"}
                  </p>
                </div>
                <button
                  type="button"
                  className="pp-upload-modal-close"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                  aria-label="Close"
                >
                  <FiX />
                </button>
              </div>

              <div
                className={`pp-upload-dropzone${uploadDragOver ? " pp-upload-dropzone--active" : ""}`}
                onDragOver={handleUploadDragOver}
                onDragLeave={handleUploadDragLeave}
                onDrop={handleUploadDrop}
              >
                <FiUploadCloud className="pp-upload-dropzone-icon" />
                <p className="pp-upload-dropzone-title">
                  {uploadDragOver ? "Drop Excel file to upload" : "Drag and drop Excel file here"}
                </p>
                <p className="pp-upload-dropzone-hint">.xlsx or .xls · replaces current project rows</p>
                <div className="pp-upload-dropzone-or">or</div>
                <button
                  type="button"
                  className="stock-inv-btn stock-inv-btn-primary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || !selectedProject}
                >
                  <FiUploadCloud /> {isUploading ? "Uploading…" : "Choose file"}
                </button>
              </div>

              <div className="pp-upload-modal-footer">
                <button
                  type="button"
                  className="stock-inv-btn stock-inv-btn-ghost"
                  onClick={closeUploadModal}
                  disabled={isUploading}
                >
                  Close
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <div className="sub-view-card stock-inv-card pp-data-card">
        <div className="stock-inv-table-wrap">
          <table className="users-table stock-inv-table pp-data-table">
            <thead>
              <tr>
                <th className="pp-col-sr">Sr.</th>
                <th>Activity</th>
                <th className="pp-col-code">Activity Code</th>
                <th className="pp-col-ag">Account Group</th>
                <th>Description</th>
                <th className="pp-col-code">Labor Tracking Code</th>
                <th className="pp-col-qty">Quantity</th>
                <th className="pp-col-uom">UOM</th>
                <th className="stock-inv-actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!selectedProject ? (
                <tr>
                  <td colSpan={9} className="users-table-empty">
                    Choose a project to manage its Production Performance upload data.
                  </td>
                </tr>
              ) : rowsLoading ? (
                <tr>
                  <td colSpan={9} className="users-table-empty">
                    Loading uploaded Excel data…
                  </td>
                </tr>
              ) : loadError ? (
                <tr>
                  <td colSpan={9} className="users-table-empty">
                    {loadError}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="users-table-empty">
                    No uploaded data for this project. Add rows manually or upload an Excel sheet.
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.localKey}>
                    <td className="pp-col-sr">
                      <span className="pp-row-sr">{index + 1}</span>
                    </td>
                    <td>
                      <input
                        value={row.scope}
                        onChange={(e) => updateRow(index, { scope: e.target.value })}
                        placeholder="e.g. RD - LWIC"
                      />
                    </td>
                    <td>
                      <input
                        value={row.activity_code}
                        onChange={(e) => updateRow(index, { activity_code: e.target.value })}
                        placeholder="010-260"
                      />
                    </td>
                    <td>
                      <select
                        value={row.account_group}
                        onChange={(e) => updateRow(index, { account_group: e.target.value })}
                      >
                        {!ACCOUNT_GROUPS.includes(row.account_group) && row.account_group ? (
                          <option value={row.account_group}>{row.account_group}</option>
                        ) : null}
                        {ACCOUNT_GROUPS.map((group) => (
                          <option key={group} value={group}>
                            {group}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        value={row.description}
                        onChange={(e) => updateRow(index, { description: e.target.value })}
                        placeholder="Description"
                      />
                    </td>
                    <td>
                      <input
                        value={row.code}
                        onChange={(e) => updateRow(index, { code: e.target.value })}
                        placeholder="Labor code"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        step="any"
                        value={row.quantity}
                        onChange={(e) => updateRow(index, { quantity: e.target.value })}
                        placeholder="0"
                      />
                    </td>
                    <td>
                      <input
                        value={row.uom}
                        onChange={(e) => updateRow(index, { uom: e.target.value })}
                        placeholder="HR / SF / Days"
                      />
                    </td>
                    <td className="stock-inv-actions-col">
                      <button
                        type="button"
                        className="stock-inv-icon-btn stock-inv-icon-btn-danger"
                        title="Remove row"
                        onClick={() => setRowDeleteIndex(index)}
                      >
                        <FiTrash2 />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        isOpen={deleteAllOpen}
        title="Delete all uploaded data?"
        message={
          selectedProject
            ? `Delete all Production Performance Excel rows for ${selectedProject.name} (${selectedProject.projectNo})? This cannot be undone.`
            : ""
        }
        confirmLabel="Delete all"
        isDestructive
        isLoading={isDeletingAll}
        onConfirm={handleDeleteAll}
        onClose={() => {
          if (!isDeletingAll) setDeleteAllOpen(false);
        }}
      />

      <ConfirmDialog
        isOpen={rowDeleteIndex != null}
        title="Remove row?"
        message="Remove this row from the editor? Click Save to persist the change."
        confirmLabel="Remove"
        isDestructive
        onConfirm={confirmDeleteRow}
        onClose={() => setRowDeleteIndex(null)}
      />
    </div>
  );
}

export default ProjectsProductionPerformanceDataManage;
